export type DomAttrs = {
  class?: string;
  id?: string;
  href?: string;
  type?: 'button' | 'reset' | 'submit';
  text?: string;
  html?: string;
  role?: string;
  'aria-label'?: string;
  'aria-pressed'?: string;
  'aria-live'?: 'off' | 'polite' | 'assertive';
  disabled?: boolean;
  title?: string;
};

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: DomAttrs = {},
  ...children: (Node | string | null | undefined)[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (attrs.class) node.className = attrs.class;
  if (attrs.id) node.id = attrs.id;
  if (attrs.href && 'href' in node) (node as HTMLAnchorElement).href = attrs.href;
  if (attrs.type && node instanceof HTMLButtonElement) node.type = attrs.type;
  if (attrs.role) node.setAttribute('role', attrs.role);
  if (attrs['aria-label']) node.setAttribute('aria-label', attrs['aria-label']);
  if (attrs['aria-pressed']) node.setAttribute('aria-pressed', attrs['aria-pressed']);
  if (attrs['aria-live']) node.setAttribute('aria-live', attrs['aria-live']);
  if (attrs.title) node.title = attrs.title;
  if (attrs.disabled && node instanceof HTMLButtonElement) node.disabled = true;
  if (attrs.html) node.innerHTML = attrs.html;
  else if (attrs.text) node.textContent = attrs.text;
  for (const child of children) {
    if (child == null) continue;
    node.appendChild(typeof child === 'string' ? document.createTextNode(child) : child);
  }
  return node;
}

export function btn(
  label: string,
  options: {
    variant?: 'primary' | 'ghost' | 'danger';
    class?: string;
    type?: 'button' | 'reset' | 'submit';
    disabled?: boolean;
  } = {},
  onClick?: () => void
): HTMLButtonElement {
  const classes = [
    'btn',
    options.variant === 'primary' ? 'btn-primary' : '',
    options.variant === 'ghost' ? 'btn-ghost' : '',
    options.variant === 'danger' ? 'btn-danger' : '',
    options.class ?? '',
  ]
    .filter(Boolean)
    .join(' ');
  const button = el('button', {
    type: options.type ?? 'button',
    class: classes,
    text: label,
    disabled: options.disabled,
  });
  if (onClick) button.addEventListener('click', onClick);
  return button;
}
